import { useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { GLOBAL_SEARCH_URL_PARAM } from '../utils/constants';

function filtersEqual(
  a: Record<string, string[] | null>,
  b: Record<string, string[] | null>
): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  for (const k of aKeys) {
    const av = a[k];
    const bv = b[k];
    if (av == null || bv == null) {
      if (av !== bv) {
        return false;
      }
      continue;
    }
    if (av.length !== bv.length) {
      return false;
    }
    for (const [i, element] of av.entries()) {
      if (element !== bv[i]) {
        return false;
      }
    }
  }
  return true;
}

export const useSearchParamsByKeys = (
  keys: string[] = []
): Record<string, string[] | null> => {
  const [params] = useSearchParams();
  const prevRef = useRef<Record<string, string[] | null>>({});

  const filters = useMemo(() => {
    const filters: Record<string, string[] | null> = {};
    for (const k of keys) {
      const values = params.getAll(k).filter((value) => value !== '');
      const uniqueValues = [...new Set(values)];

      // Officially JSON stringified params are not supported, but they are still used.
      // We need to work around this case to support global_search.
      // As JSON.parse might return other types then string (e.g. for numbers), the
      // result is wrapped into a string again.
      filters[k] =
        k === GLOBAL_SEARCH_URL_PARAM
          ? uniqueValues.map((value) => {
              try {
                return value != null ? String(JSON.parse(value)) : value;
              } catch {
                return value;
              }
            })
          : uniqueValues;
    }

    // Return the previous reference when values haven't changed to avoid
    // cascading re-renders through downstream useMemo / useEffect consumers.
    if (filtersEqual(prevRef.current, filters)) {
      return prevRef.current;
    }
    prevRef.current = filters;
    return filters;
  }, [params, keys]);

  return filters;
};
